import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "calculator__form" }
const _hoisted_2 = { class: "calculator__form--postcode" }
const _hoisted_3 = { class: "calculator__form--city" }
const _hoisted_4 = { class: "calculator__form--street" }
const _hoisted_5 = { class: "calculator__form--house-number" }
const _hoisted_6 = { class: "row g-0 align-items-center" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "col-auto col-mh" }, null, -1)
const _hoisted_8 = { class: "col" }
const _hoisted_9 = { class: "calculator__form--usage" }
const _hoisted_10 = {
  key: 0,
  class: "calculator__form--customer-type"
}
const _hoisted_11 = {
  key: 1,
  class: "calculator__form--is-customer"
}
const _hoisted_12 = { class: "calculator__form-submit" }

import BaseButton from '@/components/BaseButton.vue';
import BaseCalculatorBranchSelection from '@/components/BaseCalculator/BaseCalculatorBranchSelection.vue';
import Checkbox from '@/components/Checkbox.vue';
import BranchInterface from '@/interfaces/BranchInterface';
import CalculatorModelInterface from '@/interfaces/CalculatorModelInterface';
import {
  reactive,
  computed,
  watch,
  ref,
  onBeforeMount, nextTick, onBeforeUnmount, WatchStopHandle,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  EnetAPI,
  ProductAPI,
} from '@/api';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import BaseCalculatorUsageField from '@/components/BaseCalculator/BaseCalculatorUsageField.vue';
import CityInterface from '@/interfaces/CityInterface';
import StreetInterface from '@/interfaces/StreetInterface';
import ProductPost from '@/interfaces/ProductPostInterface';
import { env } from '@/helpers';

type callbackProp = (data: CalculatorModelInterface, valid: boolean) => void;


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseCalculator',
  props: {
    productId: {},
    forceVisibility: { type: [String, Number, Boolean], default: false },
    isBusinessActive: { type: [String, Boolean], default: false },
    branches: { default: 'all' },
    postcode: {},
    buttonLabel: { default: 'Tarif finden' },
    options: { default: () => ({}) },
    wide: { type: Boolean },
    hideSubmit: { type: Boolean },
    callback: {}
  },
  setup(__props: any) {

const props = __props;

const router = useRouter();
const route = useRoute();
const store = useStore();
const root = ref<InstanceType<typeof HTMLElement>>();

const isVisible = computed<boolean>(() => (env('CALCULATOR_ACTIVE') as boolean || [true, 'true', 1].includes((props.forceVisibility || 0))));

const allowedBranchesKeys = computed(() => (Array.isArray(props.branches)
  ? props.branches
  : `${props.branches || 'all'}`.split(',')));

const allowedBranches = computed<BranchInterface[]>(
  () => store.getters['branches/allWithUrls']
    .filter((branch: BranchInterface) => allowedBranchesKeys.value.length === 0
        || allowedBranchesKeys.value.includes(branch.slug)
        || allowedBranchesKeys.value.includes('all'))
    .sort((branch: BranchInterface, branchB: BranchInterface) => allowedBranchesKeys.value.findIndex((key) => key === branch.slug) - allowedBranchesKeys.value.findIndex((key) => key === branchB.slug)),
);

const product = ref<ProductPost>();

const valid = reactive({
  postcode: false,
  street: false,
  houseNumber: false,
  usage: false,
});

const hasFocusedOutOnce = reactive({
  postcode: false,
  city: false,
  street: false,
  houseNumber: false,
  usage: false,
});

const initialBranch = props.options.branch || (
  allowedBranches.value.length > 1 ? undefined : (allowedBranches.value[0]?.slug || 'strom')
);

const getInitialUsageByBranch = (branch?: string) => {
  switch (branch) {
    case 'gas':
      return parseInt(`${route.params.usage || 12000}`, 10);
    default:
      return parseInt(`${route.params.usage || 2500}`, 10);
  }
};

const form = ref<CalculatorModelInterface>({
  ...{
    productId: props.productId,
    branch: initialBranch,
    postcode: undefined,
    city: undefined,
    street: undefined,
    houseNumber: undefined,
    usage: getInitialUsageByBranch(initialBranch),
    business: props.isBusinessActive === 'true',
    isCustomer: false,
  },
  ...props.options,
});

const postcodeLoaded = ref<string | number>('');
const preselectedPostcodeSet = ref(false);
const selectableCities = ref<CityInterface[]>([]);
const selectableStreets = ref<StreetInterface[]>([]);

const showValidCheck = computed(() => ({
  postcode: hasFocusedOutOnce.postcode ? valid.postcode : undefined,
  city: hasFocusedOutOnce.city ? !!form.value.city : undefined,
  street: hasFocusedOutOnce.street ? !!form.value.street : undefined,
  houseNumber: hasFocusedOutOnce.houseNumber ? valid.houseNumber : undefined,
  usage: hasFocusedOutOnce.usage ? valid.usage : undefined,
}));

const selectedBranch = ref<BranchInterface>();

watch(() => selectedBranch.value?.slug, (newValue) => {
  form.value.usage = getInitialUsageByBranch(newValue);
});

const isInternet = computed<boolean>(() => selectedBranch.value?.mainCategory.key === 'internet');

const isFetching = ref(false);
const fetchCitiesAndStreets = ($postcode: string | number) => {
  if (`${$postcode}`.length === 5 && $postcode !== postcodeLoaded.value) {
    selectableCities.value = [];
    selectableStreets.value = [];
    valid.postcode = false;
    hasFocusedOutOnce.city = false;
    hasFocusedOutOnce.street = false;
    hasFocusedOutOnce.houseNumber = false;
    hasFocusedOutOnce.usage = false;

    postcodeLoaded.value = $postcode as string;

    isFetching.value = true;
    EnetAPI.getCitiesAndStreets($postcode).then((data) => {
      selectableCities.value = data.cities;
      selectableStreets.value = data.streets;
      if (!((props.options.postcode === $postcode) && !preselectedPostcodeSet.value)) {
        form.value.city = selectableCities.value[0].city;
        if (selectableCities.value.length === 1) {
          form.value.city = selectableCities.value[0].city;
          hasFocusedOutOnce.city = true;
        } else {
          form.value.city = undefined;
        }
        if (selectableStreets.value.length === 1) {
          hasFocusedOutOnce.street = true;
          form.value.street = selectableStreets.value[0].street;
        } else {
          form.value.street = undefined;
        }
        preselectedPostcodeSet.value = true;
      }
      valid.postcode = true;
    }).catch(() => {
      valid.postcode = false;
    }).finally(() => {
      isFetching.value = false;
      hasFocusedOutOnce.postcode = true;
    });
  }
};

watch(() => form.value.postcode, ($newPostcode) => {
  fetchCitiesAndStreets($newPostcode as string);

  if (!selectedBranch.value) {
    // eslint-disable-next-line prefer-destructuring
    selectedBranch.value = allowedBranches.value[0];
  }
});

if (form.value.postcode && `${form.value.postcode}`.length === 5) {
  fetchCitiesAndStreets(form.value.postcode);
}

const cityOptions = computed(() => [...selectableCities.value].map(($s) => ({
  label: $s.city,
  value: $s.city,
})));

const streetsOptions = computed(() => [...selectableStreets.value].map(($s) => ({
  label: $s.street,
  value: $s.street,
})));

const canSubmit = computed(() => selectedBranch.value
    && allowedBranches.value.filter((branchOption) => branchOption.slug === selectedBranch.value?.slug).length >= 0
    && `${form.value.postcode || ''}`.trim().length === 5
    && form.value.city !== undefined
    && form.value.street !== undefined
    && `${form.value.houseNumber || ''}`.trim().length >= 1
    && (isInternet.value || parseInt(`${form.value.usage || ''}`, 10) > 0));

const showCity = computed<boolean>(() => (
  `${form.value.postcode || ''}`.trim().length === 5
    && cityOptions.value.length > 0
));
const showStreet = computed<boolean>(() => (
  showCity.value
    && streetsOptions.value.length > 0
));
const showHouseNumber = computed<boolean>(() => showStreet.value);
const showUsage = computed<boolean>(() => showStreet.value && !isInternet.value);
const showSubmitButton = computed<boolean>(() => !props.hideSubmit && canSubmit.value);

const submit = () => {
  let path = '/preisanzeige/';

  if (!selectedBranch.value) return;
  if (!form.value.postcode) return;
  if (!form.value.city) return;
  if (!form.value.street) return;
  if (!form.value.houseNumber) return;

  if (props.callback) {
    props.callback(form.value, canSubmit.value);

    return;
  }

  const params: Record<string, string> = {
    stadt: form.value.city as string,
    strasse: form.value.street as string,
    hausnummer: form.value.houseNumber as string,
  };

  if (form.value.business) {
    params.gewerbe = '1';
  }

  if (form.value.isCustomer) {
    params.energiekunde = '1';
  }

  if (form.value.productId) {
    params.pid = `${form.value.productId}`;
  }

  const urlParams = new URLSearchParams(params);

  if (isInternet.value) {
    path += `${selectedBranch.value?.slug}/${form.value.postcode}/?${urlParams.toString()}`;
  } else {
    path += `${selectedBranch.value?.slug}/${form.value.postcode}/${form.value.usage}/?${urlParams.toString()}`;
  }

  router.push(path);
};

const checkPostcodeValid = () => {
  valid.postcode = `${form.value.postcode || ''}`.trim().length === 5 && selectableCities.value.length > 0;
};

const checkUsageValid = () => {
  valid.usage = parseInt(`${form.value.usage || ''}`, 10) > 0;
};

watch(() => form.value.usage, checkUsageValid);

const checkHousenumberValid = () => {
  valid.houseNumber = `${form.value.houseNumber || ''}`.trim().length >= 1;
};

let callbackWatcher: WatchStopHandle;

onBeforeMount(() => {
  if (props.productId !== undefined) {
    ProductAPI.get(props.productId).then((apiProduct) => {
      product.value = apiProduct;

      if (apiProduct) {
        form.value.branch = apiProduct.branch || 'power';

        const apiProductBranch = allowedBranches.value.find((branch) => branch.slug === form.value.branch);
        if (apiProductBranch) {
          selectedBranch.value = apiProductBranch;
        }
      }
    });
  }

  if (props.hideSubmit && props.callback) {
    callbackWatcher = watch(() => form, () => {
      nextTick(() => {
        if (props.callback) {
          props.callback(form.value, canSubmit.value);
        }
      });
    }, {
      deep: true,
    });
  }
});

onBeforeUnmount(() => {
  if (callbackWatcher) {
    callbackWatcher();
  }
});

watch(() => allowedBranches.value, () => {
  if (props.options.branch) {
    const preselectedBranch = allowedBranches.value.find((branch) => branch.slug === props.options.branch);

    if (preselectedBranch) {
      selectedBranch.value = preselectedBranch;
    }
  }
}, {
  deep: true,
  immediate: true,
});

return (_ctx: any,_cache: any) => {
  return (isVisible.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["calculator", {
        wide: _ctx.wide,
      }]),
        ref_key: "root",
        ref: root
      }, [
        _createVNode(BaseCalculatorBranchSelection, {
          "allowed-branches": allowedBranches.value,
          modelValue: selectedBranch.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedBranch).value = $event))
        }, null, 8, ["allowed-branches", "modelValue"]),
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(InputField, {
              label: "Deine Postleitzahl",
              "only-numeric": true,
              "error-numeric": "Ungültige Postleitzahl",
              "max-length": 5,
              "min-length": 5,
              "show-loader": isFetching.value,
              "is-valid": showValidCheck.value.postcode,
              "valid-color": [selectedBranch.value?.customColor.primary, selectedBranch.value?.customColor.secondary],
              modelValue: form.value.postcode,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.postcode) = $event)),
              onKeyup: checkPostcodeValid
            }, null, 8, ["show-loader", "is-valid", "valid-color", "modelValue"])
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_3, [
            _createVNode(SelectField, {
              label: "Deine Stadt*",
              modelValue: form.value.city,
              "onUpdate:modelValue": [
                _cache[2] || (_cache[2] = ($event: any) => ((form.value.city) = $event)),
                _cache[3] || (_cache[3] = ($event: any) => (hasFocusedOutOnce.city = true))
              ],
              readonly: selectableCities.value.length <= 1,
              options: cityOptions.value,
              "is-valid": showValidCheck.value.city,
              "valid-color": [selectedBranch.value?.customColor.primary, selectedBranch.value?.customColor.secondary]
            }, null, 8, ["modelValue", "readonly", "options", "is-valid", "valid-color"])
          ], 512), [
            [_vShow, showCity.value]
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_4, [
            _createVNode(SelectField, {
              label: "Straße*",
              modelValue: form.value.street,
              "onUpdate:modelValue": [
                _cache[4] || (_cache[4] = ($event: any) => ((form.value.street) = $event)),
                _cache[5] || (_cache[5] = ($event: any) => (hasFocusedOutOnce.street = true))
              ],
              options: streetsOptions.value,
              search: true,
              "is-valid": showValidCheck.value.street,
              "valid-color": [selectedBranch.value?.customColor.primary, selectedBranch.value?.customColor.secondary]
            }, null, 8, ["modelValue", "options", "is-valid", "valid-color"])
          ], 512), [
            [_vShow, showStreet.value]
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _hoisted_7,
              _createElementVNode("div", _hoisted_8, [
                _createVNode(InputField, {
                  label: "Hausnummer*",
                  type: "text",
                  modelValue: form.value.houseNumber,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.value.houseNumber) = $event)),
                  "is-valid": showValidCheck.value.houseNumber,
                  "valid-color": [selectedBranch.value?.customColor.primary, selectedBranch.value?.customColor.secondary],
                  onKeyup: checkHousenumberValid,
                  onFocusout: _cache[7] || (_cache[7] = ($event: any) => (hasFocusedOutOnce.houseNumber = true))
                }, null, 8, ["modelValue", "is-valid", "valid-color"])
              ])
            ])
          ], 512), [
            [_vShow, showHouseNumber.value]
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_9, [
            _createVNode(BaseCalculatorUsageField, {
              label: "Dein Verbrauch*",
              modelValue: form.value.usage,
              "onUpdate:modelValue": [
                _cache[8] || (_cache[8] = ($event: any) => ((form.value.usage) = $event)),
                _cache[9] || (_cache[9] = ($event: any) => (hasFocusedOutOnce.usage = true))
              ],
              branch: selectedBranch.value?.mainCategory.key || 'power',
              "is-valid": showValidCheck.value.usage,
              "valid-color": [selectedBranch.value?.customColor.primary, selectedBranch.value?.customColor.secondary],
              onKeyup: checkUsageValid,
              onFocusout: _cache[10] || (_cache[10] = () => { checkUsageValid(); hasFocusedOutOnce.usage = true })
            }, null, 8, ["modelValue", "branch", "is-valid", "valid-color"])
          ], 512), [
            [_vShow, showUsage.value]
          ]),
          (showHouseNumber.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createVNode(Checkbox, {
                  modelValue: form.value.business,
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((form.value.business) = $event)),
                  light: ""
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Ich bin Geschäftskund:in ")
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]))
            : _createCommentVNode("", true),
          (isInternet.value && _ctx.wide)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createVNode(Checkbox, {
                  modelValue: form.value.isCustomer,
                  "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((form.value.isCustomer) = $event)),
                  light: ""
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Ich bin Strom/Gas Kund:in ")
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]))
            : _createCommentVNode("", true),
          _withDirectives(_createElementVNode("div", _hoisted_12, [
            _createVNode(BaseButton, {
              branch: selectedBranch.value,
              width: "full",
              icon: "fa-solid fa-arrow-right",
              style: _normalizeStyle({
              background: `linear-gradient(90deg, ${selectedBranch.value?.customColor.primary}, ${selectedBranch.value?.customColor.secondary})`,
            }),
              onClick: _withModifiers(submit, ["prevent"])
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.buttonLabel), 1)
              ]),
              _: 1
            }, 8, ["branch", "style"])
          ], 512), [
            [_vShow, showSubmitButton.value]
          ])
        ])
      ], 2))
    : _createCommentVNode("", true)
}
}

})